// Lightweight client-side backup export — JSON (full) and CSV (flat table).
import { SLOTS } from "./slots";
import { DEFAULT_PAYMENT_LABELS } from "./finance";

const STATUS_LABEL = {
  offer: "Angebot",
  planned: "Geplant",
  active: "In Aufbau",
  waiting: "Warten auf",
  paused: "Pausiert",
  billed: "Abgerechnet",
  live: "Live",
  completed: "Abgeschlossen",
  archived: "Archiviert",
};

const PAYMENT_STATUS_LABEL = {
  planned: "Geplant",
  due: "Fällig",
  invoiced: "Berechnet",
  paid: "Bezahlt",
};

function todayIsoFile() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function triggerDownload(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------------- JSON ---------------- */

export function exportProjectsJson(projects) {
  const payload = {
    app: "Wandplaner",
    exported_at: new Date().toISOString(),
    schema_version: 1,
    project_count: projects.length,
    projects,
  };
  triggerDownload(
    `wandplaner-backup-${todayIsoFile()}.json`,
    JSON.stringify(payload, null, 2),
    "application/json;charset=utf-8",
  );
}

/* ---------------- CSV ---------------- */

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",;\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function row(values) {
  return values.map(csvEscape).join(";");
}

const CSV_HEADER = [
  "Projekt",
  "Projekt-Referenz",
  "Kürzel",
  "Slot",
  "Slot-Farbe",
  "Status",
  "Startdatum",
  "Enddatum",
  "Meilenstein-Titel",
  "Meilenstein-Datum",
  "Zahlungstyp",
  "Zahlungs-Label",
  "Betrag (EUR)",
  "Rechnungsdatum",
  "Fälligkeitsdatum",
  "paid_date",
  "Zahlungsstatus",
  "service_note",
  "planning_notes",
];

function flattenProjectRows(p) {
  const out = [];
  const base = [
    p.name || "",
    p.client || "",
    p.planner_short_label || "",
    p.slot ?? "",
    SLOTS[(p.slot || 1) - 1]?.color || "",
    STATUS_LABEL[p.status] || p.status || "",
    p.start_date || "",
    p.end_date || "",
    p.milestone_title || "",
    p.milestone_date || "",
  ];

  const payments = Array.isArray(p.payments) ? p.payments : [];
  const extras = Array.isArray(p.extra_payments) ? p.extra_payments : [];
  const sceList = Array.isArray(p.service_contract_entries) ? p.service_contract_entries : [];
  const sc = p.service_contract;
  const hasSc =
    sc && (sc.yearly_amount != null || sc.invoice_date || sc.due_date);
  const serviceNote = sc?.note || "";
  const planningStr = (() => {
    const arr = Array.isArray(p.planning_notes) ? p.planning_notes : [];
    return arr
      .filter((n) => n && (n.title || "").trim())
      .map((n) => {
        const t = String(n.title).trim();
        const parts = [];
        if (n.date) {
          const ds = String(n.date).slice(0, 10).split("-");
          const de = ds.length === 3 ? `${ds[2]}.${ds[1]}.${ds[0]}` : n.date;
          parts.push(`(${de})`);
        }
        const meta = [];
        if (n.planner_icon) meta.push(`planner_icon=${n.planner_icon}`);
        meta.push(`show_in_planner=${n.show_in_planner ? "true" : "false"}`);
        parts.push(`[${meta.join("; ")}]`);
        return `${t} ${parts.join(" ")}`;
      })
      .join(" | ");
  })();

  if (payments.length === 0 && extras.length === 0 && !hasSc && sceList.length === 0) {
    out.push(row([...base, "", "", "", "", "", "", "", serviceNote, planningStr]));
    return out;
  }

  payments.forEach((pay, idx) => {
    out.push(
      row([
        ...base,
        "Zahlung",
        pay?.label || DEFAULT_PAYMENT_LABELS[idx] || `Zahlung ${idx + 1}`,
        pay?.amount ?? "",
        pay?.invoice_date || "",
        pay?.due_date || "",
        pay?.paid_date || "",
        PAYMENT_STATUS_LABEL[pay?.status] || pay?.status || "",
        serviceNote,
        planningStr,
      ]),
    );
  });

  extras.forEach((pay, idx) => {
    out.push(
      row([
        ...base,
        "Zusatz",
        pay?.label || `Zusatz ${idx + 1}`,
        pay?.amount ?? "",
        pay?.invoice_date || "",
        pay?.due_date || "",
        pay?.paid_date || "",
        PAYMENT_STATUS_LABEL[pay?.status] || pay?.status || "",
        serviceNote,
        planningStr,
      ]),
    );
  });

  if (hasSc) {
    out.push(
      row([
        ...base,
        "Servicevertrag",
        "Servicevertrag (jährlich)",
        sc.yearly_amount ?? "",
        sc.invoice_date || "",
        sc.due_date || "",
        sc.paid_date || "",
        PAYMENT_STATUS_LABEL[sc.status] || sc.status || "",
        serviceNote,
        planningStr,
      ]),
    );
  }

  sceList.forEach((pay) => {
    if (!pay) return;
    out.push(
      row([
        ...base,
        "Servicevertrag",
        pay.label || "Servicevertrag (jährlich)",
        pay.amount ?? "",
        pay.invoice_date || "",
        pay.due_date || "",
        pay.paid_date || "",
        PAYMENT_STATUS_LABEL[pay.status] || pay.status || "",
        serviceNote,
        planningStr,
      ]),
    );
  });

  return out;
}

export function exportProjectsCsv(projects) {
  const lines = [row(CSV_HEADER)];
  for (const p of projects) lines.push(...flattenProjectRows(p));
  // BOM so Excel auto-detects UTF-8 (umlauts, EUR-sign).
  const content = "\uFEFF" + lines.join("\r\n") + "\r\n";
  triggerDownload(
    `wandplaner-backup-${todayIsoFile()}.csv`,
    content,
    "text/csv;charset=utf-8",
  );
}
